/**
 * «Mi cuenta»: tres solapas, una pantalla.
 *
 * Esto era «Más», y era una tira con cuatro asuntos sin relación apilados —la
 * cuenta, el consultorio, la facturación y cerrar sesión—: había que bajar por
 * todo para llegar a lo de abajo, y los avisos del teléfono quedaban enterrados
 * donde nadie los iba a encontrar.
 *
 * El dueño (8-set-2026) propuso el remedio y es el que está acá: «podría haber
 * como un tipo slider entre los datos personales, y ahí mismo la información de
 * facturación… y mi consultorio como otro tab más». Tres solapas en vez de tres
 * pantallas: no hay a dónde ir ni de dónde volver, y se cambia de una a otra
 * con el pulgar sin perder de vista dónde se está.
 *
 * El control es el mismo `.segmented` que ya usan el directorio de doctores y
 * la galería de facturación, no uno nuevo.
 *
 * «Cómo pagar» tampoco vive acá desde el 8-set: se mudó a Cobros, que es donde
 * se busca — ver `ComoPagar.tsx`.
 */

import { useState } from 'react';
import { useApi } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import { haptic } from '@/lib/haptics';
import { Async, Avatar, Button, KeyValueRow } from '@/components/ui';
import { TarjetasDeCuenta } from '@/features/profile/MyProfile';
import {
  ConsultorioMembers, ConsultorioReparto, type ConsultorioMember, type SubscriptionAllocation,
} from './ConsultorioHub';
import { Facturacion } from './Facturacion';

interface UnitDetail {
  unit: { id: string; unit_code: string; name: string | null; address: string | null; location_name: string | null };
  members: ConsultorioMember[];
  availability: { weekday: number; start_time: string; end_time: string }[];
  subscriptions: SubscriptionAllocation[];
}

const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

/* Rótulos cortos a propósito: a 375 px, tres solapas comparten el ancho de la
   pantalla y «Datos personales» no entra sin partirse. */
const SOLAPAS = [
  { key: 'cuenta', label: 'Cuenta' },
  { key: 'facturacion', label: 'Facturación' },
  { key: 'consultorio', label: 'Consultorio' },
] as const;

type Solapa = typeof SOLAPAS[number]['key'];

export function PortalMore() {
  const { user, logout } = useSession();
  const [solapa, setSolapa] = useState<Solapa>('cuenta');

  const home = useApi<{ units: { id: string }[] }>('/portal/home');
  const unitId = home.data?.units[0]?.id;
  /* Solo se pide cuando se abre la solapa: al entrar a la pantalla, quien
     viene a cambiar su contraseña no tiene por qué esperar una consulta del
     consultorio que quizá no mire nunca. */
  const unit = useApi<UnitDetail>(solapa === 'consultorio' && unitId ? `/portal/units/${unitId}` : null);

  return (
    <div className="portal-split msec--split">
      <div>
        <section className="msec">
          {/* La persona arriba de todo y fuera de las solapas: es de quién es
              esta pantalla, no una de sus partes. */}
          <div className="mcard" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Avatar
              src={user?.photo_key ? `/api/me/photo?v=${user.photo_key}` : null}
              name={user?.full_name ?? ''}
              size={52}
            />
            <div style={{ minWidth: 0 }}>
              <div className="strong">{user?.full_name}</div>
              <div className="small muted" style={{ overflowWrap: 'anywhere' }}>{user?.email ?? '—'}</div>
            </div>
          </div>

          <div
            className="segmented mt-2"
            role="group"
            aria-label="Qué parte de tu cuenta"
            style={{
              '--seg-n': SOLAPAS.length,
              '--seg-i': SOLAPAS.findIndex((s) => s.key === solapa),
            } as React.CSSProperties}
          >
            <div className="segmented__pill" />
            {SOLAPAS.map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={solapa === s.key}
                className={`segmented__opt${solapa === s.key ? ' is-active' : ''}`}
                onClick={() => { if (solapa !== s.key) haptic.light(); setSolapa(s.key); }} // HAPTIC_FEEDBACK
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {solapa === 'cuenta' && (
          <section className="msec">
            <TarjetasDeCuenta />
          </section>
        )}

        {solapa === 'facturacion' && (
          <Facturacion defaultLegalName={user?.full_name ?? ''} />
        )}

        {solapa === 'consultorio' && unitId && (
          <section className="msec">
            <Async query={unit}>
              {(data) => (
                <>
                  <div className="mcard mcard--par">
                    <KeyValueRow label="Consultorio" style={{ paddingTop: 0 }}>{data.unit.unit_code}</KeyValueRow>
                    {data.unit.location_name && (
                      <KeyValueRow label="Ubicación" style={{ borderBottom: 0 }}>{data.unit.location_name}</KeyValueRow>
                    )}
                  </div>

                  {data.availability.length > 0 && (
                    <>
                      <div className="msec__title mt-2">Horario de limpieza</div>
                      <div className="mcard mcard--par">
                        {data.availability.map((a, i) => (
                          <KeyValueRow key={`${a.weekday}-${a.start_time}-${i}`} label={WEEKDAYS[a.weekday]}
                            style={i === data.availability.length - 1 ? { borderBottom: 0 } : undefined}>
                            {a.start_time}–{a.end_time}
                          </KeyValueRow>
                        ))}
                      </div>
                    </>
                  )}

                  {/* El hub que pidió el dueño (2-sep-2026): quiénes forman el
                      consultorio y cómo se reparte el pago. */}
                  <ConsultorioMembers unitId={unitId} unitCode={data.unit.unit_code} members={data.members} />
                  <ConsultorioReparto unitId={unitId} unitCode={data.unit.unit_code} subscriptions={data.subscriptions} />
                </>
              )}
            </Async>
          </section>
        )}
      </div>

      <aside className="msec--split-lateral">
        <section className="msec">
          <Button block onClick={() => void logout()}>Cerrar sesión</Button>
        </section>
      </aside>
    </div>
  );
}
